import { CardContainer } from "@src/components/cards";
import { useAuthContext } from "@src/context";
import AdminHooks from "@src/context/hooks/adminHooks";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { Button, Icon, Loader, Modal } from "@src/components/atoms";

import { useAdminContext } from "@src/context/adminContext";
import { BehaviourForm } from "@src/components/forms/admin/BehaviourForm";
import BehavioursTable from "@src/components/tables/admin/BehavioursTable";
import { normalizeEmptyStringsToNull } from "@src/components/forms/admin/AdminHelpers";

export const BehavioursSection = ({ customerId, customerCode, industry }) => {
  const auth = useAuthContext();
  const [isEditing, setIsEditing] = useState(null);
  const [selectedBehaviour, setSelectedBehaviour] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const {
    data: behaviours,
    isLoading,
    isPending,
    isSettled,
    isError,
    //error: errorAll,
  } = AdminHooks.useGetBehaviours(auth.activeRole, customerCode);

  const mutationCallback = (status) => {
    console.log("submitted and processed");
  };

  const updateMutation = AdminHooks.useUpdateBehaviour(mutationCallback);
  const createMutation = AdminHooks.useCreateBehaviour(mutationCallback);
  const deleteMutation = AdminHooks.useDeleteBehaviour(mutationCallback);

  const onBehaviourClick = useCallback((id) => {
    setSelectedBehaviour(id);
  }, []);

  const filteredBehaviours = useMemo(() => {
    const type =
      industry.toLowerCase() === "aviation"
        ? "FLIGHT"
        : industry.toLowerCase() === "shipping"
          ? "SHIPPING"
          : null;
    const filtered = type
      ? behaviours?.filter(
          (b) => b.eventType?.toLowerCase() === type?.toLowerCase(),
        )
      : behaviours;
    return filtered;
  }, [behaviours, industry]);

  const handleToggle = useCallback(
    async (acronym) => {
      const behaviour = filteredBehaviours.find((b) => b.acronym === acronym);
      if (behaviour.enabled) {
        console.log(`Attempting to delete ${acronym}`);
        if (
          window.confirm(
            `Delete ${acronym} from ${customerCode} and permenantly lose its contents?`,
          )
        ) {
          await deleteMutation.mutateAsync(behaviour.customerBehaviourId);
        }
      } else {
        setSelectedBehaviour(acronym);
      }
    },
    [filteredBehaviours],
  );

  return (
    <div className="p-4">
      {isLoading && <Loader size="2xl" />}
      {!isLoading && !!isError && (
        <div className="text-center">Error fetching behaviours...</div>
      )}

      <div className="flex  flex-wrap gap-2 justify-end items-center py-2">
        {!isLoading && (
          <>
            {!!submitted && (
              <div className="grow text-green-500 border border-1-green rounded-2xl p-2 bg-green-100">
                <p>Your changes have been submitted successfully.</p>
              </div>
            )}
            {!!error && (
              <div className="grow text-red-500 border border-1-red rounded-2xl p-2 bg-red-100">
                <p>{error}</p>
              </div>
            )}
          </>
        )}
      </div>
      {!isLoading && behaviours?.length > 0 && (
        <BehavioursTable
          behaviours={filteredBehaviours}
          onBehaviourClick={onBehaviourClick}
          handleToggle={handleToggle}
          industry={industry}
        />
      )}

      <Modal
        size="fullscreen"
        open={!!selectedBehaviour}
        handleClose={() => setSelectedBehaviour(null)}
        children={
          <div>
            <BehaviourForm
              data={
                selectedBehaviour && behaviours
                  ? behaviours.find((b) => b.acronym === selectedBehaviour)
                  : null
              }
              onSubmit={async (formData) => {
                const originalBehaviour = behaviours?.find(
                  (b) => b.acronym === formData.acronym,
                );
                const updatedBehaviour = {
                  ...formData,
                  customerId,
                  enabled: originalBehaviour.enabled,
                  customerBehaviourId: originalBehaviour.customerBehaviourId,
                };
                setError("");
                setSubmitted(false);
                try {
                  if (updatedBehaviour.enabled) {
                    console.log("Attempting to update behaviour");
                    await updateMutation.mutateAsync(updatedBehaviour);
                  } else {
                    console.log("Attempting to create behaviour");
                    await createMutation.mutateAsync(updatedBehaviour);
                  }
                  setSubmitted(true);
                } catch (err) {
                  setError("Failed to submit.");
                } finally {
                  setSubmitting(false);
                }
                setSelectedBehaviour(null);
              }}
              onCancel={() => setSelectedBehaviour(null)}
            />
          </div>
        }
      />
    </div>
  );
};
